import { IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { EditOutlined } from "@mui/icons-material";
import { ICellRendererParams } from "ag-grid-community";
import ActivityLookupMultipleComplete from "components/activity/ActivityDialog/ActivityLookupMultipleComplete";
import TwoRowsRenderer from "components/activity/cellRenderer/TwoRowsRenderer";
import ArrowPopper from "components/app/ArrowPopper";
import { TypeOption } from "components/app/BaseMultipleAutocomplete/BaseMultipleAutocomplete";
import { useMetadataByCategory } from "components/summary/hooks";
import { useAxios } from "myAxios";
import { FC, MouseEvent, useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SHARE_FROM_TYPE, SharedFrom } from "types/playbook";
import { getNameOfAConcatString, promiseErrorHandleWithMessage } from "utils";
import ActivityGridEventContext from "../context/ActivityGridEventContext";
import { mutateActivity, mutateTask } from "../utils";
import { classHOC } from "components/app/ClassWrapper";

const useStyles = makeStyles(() => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  info: {
    display: "flex",
    alignItems: "center",
  },
  main: {
    fontSize: 12,
    marginRight: 5,
    color: "#535759",
  },
  sub: {
    color: "#898989",
    fontSize: 10,
  },
  labelWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 20,
    borderRadius: 12,
    border: "1px solid #DEDEDE",
    marginLeft: 5,
  },
  txt: {
    fontSize: 11,
  },
  moreProducts: {
    padding: "10px 12px",
    width: 240,
    height: 108,
    overflowY: "auto",
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    border: "1px solid #DEDEDE",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.16), 0px 0px 1px rgba(0, 0, 0, 0.14)",
    borderRadius: 4,
  },
  infoRow: {
    display: "flex",
    marginBottom: 10,
    "&:last-of-type": {
      marginBottom: 0,
    },
  },
}));

const ProductRenderer: FC<
  Partial<ICellRendererParams> & {
    from?: SHARE_FROM_TYPE;
    enableGroup?: boolean;
  }
> = ({ data, node, column, value, from = SharedFrom.ACTIVITY, enableGroup = true }) => {
  const { data: product_lookup } = useMetadataByCategory("ACTIVITY_PRODUCT");
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const epCellRef = useRef<HTMLDivElement>(null);
  const epPopRef = useRef<HTMLDivElement>(null);
  const detailPopRef = useRef<HTMLDivElement>(null);

  const [showForm, setShowForm] = useState(false);
  const [newVal, setNewVal] = useState<string[]>([]);
  const { control } = useForm();
  const axios = useAxios();

  const val = newVal?.length ? newVal : value;

  const { activityGridEvent } = useContext(ActivityGridEventContext);
  const [productOptions, setProductOptions] = useState<TypeOption[]>([]);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        ((!epPopRef.current?.contains(e.target as Node) && epPopRef.current !== e.target) ||
          (!detailPopRef.current?.contains(e.target as Node) &&
            detailPopRef.current !== e.target)) &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setShowForm(false);
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  useEffect(() => {
    if (product_lookup) {
      setProductOptions(
        product_lookup.map((d: any) => ({
          name: d.code as string,
          value: d.value as string,
          type: d.category as string,
        }))
      );
    }
  }, [product_lookup]);

  const showMore = (event: MouseEvent<HTMLDivElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  useEffect(() => {
    if (newVal?.length && !showForm) {
      const patch = async () => {
        await axios.patch(`/api/dataservice/v1/activities/${data?.id}`, {
          products: newVal,
        });
      };
      patch().catch((e) => promiseErrorHandleWithMessage(e));
      if (from === SharedFrom.ACTIVITY) {
        mutateActivity(data?.id, activityGridEvent);
      } else {
        mutateTask(data?.taskId, activityGridEvent, data?.id);
      }
    }
  }, [showForm]);

  const handleChange = (selected: { name: string; value: string }[]) => {
    setNewVal(
      selected?.length ? selected?.map((x: { name: string; value: string }) => x.value) : []
    );
  };

  const setupProductDefaultValue = (vals: string[]) => {
    if (vals.length > 0 && vals[0].includes("%-%")) {
      return vals.map((p) => {
        return {
          name: getNameOfAConcatString(p),
          value: p,
          type: p.split("%-%")?.[0],
        };
      });
    } else {
      return vals.map((d) => productOptions.find((p) => p.value === d));
    }
  };

  if (node?.rowPinned) return null;

  return (
    <div
      style={{ display: "flex", alignItems: "center" }}
      className="hover-show-container"
      ref={epCellRef}
    >
      {showForm ? (
        <div
          ref={epPopRef}
          onKeyPress={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.stopPropagation()}
        >
          <Controller
            control={control}
            render={(props) => (
              <ActivityLookupMultipleComplete
                {...props}
                disabled={!data?.editable || data?.systemRelated}
                label=""
                category={"ACTIVITY_PRODUCT"}
                showMarginLeft={true}
                fullWidth={true}
                hideTitle={true}
                handleChange={handleChange}
                ops={productOptions}
              />
            )}
            defaultValue={setupProductDefaultValue(data?.products ?? [])}
            name={"products"}
          />
        </div>
      ) : val !== "N/A" && val?.length > 0 ? (
        <>
          <div className={classes.wrapper}>
            {enableGroup ? <TwoRowsRenderer value={val?.[0]} /> : <span>{val?.[0]}</span>}
            {val?.length > 1 ? (
              <div
                className={classes.labelWrapper}
                style={{ background: anchorEl ? "#333" : "#fff" }}
                onClick={showMore}
              >
                <p
                  className={classes.txt}
                  style={{
                    color: anchorEl ? "#fff" : "#535759",
                    cursor: "pointer",
                  }}
                >
                  + {val?.length - 1}
                </p>
              </div>
            ) : null}
            {anchorEl ? (
              <ArrowPopper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="left"
                disablePortal={false}
              >
                <div ref={detailPopRef}>
                  {val?.slice(1).map((p: string) => {
                    return (
                      <div
                        className={classes.infoRow}
                        key={p}
                        style={{ alignItems: "center" }}
                      >
                        {p.includes("%-%") ? (
                          <>
                            <p className={classes.main}>{p.split("%-%")?.[0]}</p>
                            {enableGroup ? (
                              <p className={classes.sub}>({getNameOfAConcatString(p)})</p>
                            ) : null}
                          </>
                        ) : (
                          <p className={classes.main}>{p}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ArrowPopper>
            ) : null}
          </div>
        </>
      ) : (
        <>N/A</>
      )}
      {!data?.editable ||
      data?.systemRelated ||
      (from === SharedFrom.TASK && !data?.taskId) ||
      from === SharedFrom.PLAYBOOK ||
      !((data?.editableFields as string[]) ?? []).includes(column?.getColId() ?? "") ? null : (
        <IconButton
          size="small"
          className={"hover-show"}
          onClick={() => setShowForm(true)}
          style={{ marginLeft: 10, height: "fit-content" }}
        >
          <EditOutlined style={{ color: "#00a0d1", fontSize: "1rem" }} />
        </IconButton>
      )}
    </div>
  );
};

export default classHOC(ProductRenderer);
